import { FormFieldConfig, GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import { IReport } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { FUEL_GRADES } from '../constants/fuel-grades.constants';

type ReportStoreLike = {
  formFieldRules: () => Record<string, GroupedFormData> | null;
};

export function getFormRulesForField(
  fieldId: string,
  sectionId: string,
  tabId: string,
  reportStore?: ReportStoreLike
): FormFieldConfig | undefined {
  if (!reportStore) return;

  const rules = reportStore.formFieldRules();

  if (!rules) return;

  const tabRules = rules[tabId];
  if (!tabRules) return;

  const sectionRules = tabRules[sectionId];
  if (!sectionRules) return;

  const fieldRules = sectionRules.find((field: FormFieldConfig) => field.fieldId === fieldId);
  if (!fieldRules) return;

  return fieldRules;
}

export function isFieldRequired(
  fieldId: string,
  sectionId: string,
  tabId: string,
  reportStore?: ReportStoreLike
): boolean {
  const fieldRules = getFormRulesForField(fieldId, sectionId, tabId, reportStore);
  return fieldRules?.isRequired ?? false;
}

export function isFieldReadOnly(
  fieldId: string,
  sectionId: string,
  tabId: string,
  reportStore?: ReportStoreLike
): boolean {
  const fieldRules = getFormRulesForField(fieldId, sectionId, tabId, reportStore);
  return fieldRules?.isReadOnly ?? false;
}

export const getAvailableFuelGrades = (
  carryOverReport: IReport | null,
  vesselSpec: VesselSpec | undefined
): string[] => {
  let availableFuelGrades = [] as string[];

  if (vesselSpec?.emissions) {
    Object.keys(FUEL_GRADES).forEach((fuelGrade) => {
      if (vesselSpec.emissions[`${fuelGrade}_enabled`]) {
        availableFuelGrades.push(fuelGrade);
      }
    });
  }

  if (vesselSpec?.computed?.enabled_fuel_types?.length) {
    availableFuelGrades = availableFuelGrades.concat(vesselSpec?.computed?.enabled_fuel_types);
  }

  if (carryOverReport?.consumption) {
    availableFuelGrades.push(carryOverReport?.consumption.fuelType1);
    availableFuelGrades.push(carryOverReport?.consumption.fuelType2);
    availableFuelGrades.push(carryOverReport?.consumption.fuelType3);
  }

  return availableFuelGrades.filter((fuelGrade) => !!fuelGrade);
};

export const getAvailableFuelGradeOptions = (availableFuelGrades: string[]): SelectOption[] =>
  availableFuelGrades.map((fuelGrade) => ({
    name: fuelGrade.toLocaleUpperCase(),
    code: fuelGrade,
  }));

export const getTankOptions = (vesselSpec: VesselSpec | undefined): SelectOption[] =>
  vesselSpec?.tanks.map((tank) => ({
    name: tank.name,
    code: tank.tank_id.$oid as string,
  })) ?? [];
